import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { StateService, Transition } from '@uirouter/angular';
import { Observable } from 'rxjs';
import { GeographicalMapDataService } from '../../../geographical-map/data-access/geographical-map-data.service';
import {
    GeoMapCountrySelection,
    GeoMapFeatureCollection,
    GeoMapFill,
    GeoMapRegionFeatureCollection
} from '../../../geographical-map/data-access/geographical-map.types';
import { ProjectDataService } from '../../../project-display/data-access/project-data.service';
import { IProject } from '../../../project-display/interfaces/IProject';
import { GeographicalSummaryDataService } from '../../data-access/geographical-summary-data.service';
import {
    GeographicalChartPoint,
    GeographicalCountrySummary,
    GeographicalProjectLink,
    GeographicalSector
} from '../../data-access/geographical-summary.types';

interface LegacySeedRegion {
    legacyRegionId: string;
    name: string;
    countries: Array<{ name?: string }>;
}

interface DevCountryOption {
    value: string;
    name: string;
    mapped: boolean;
}

@Component({
    selector: 'app-geographical-summary-page',
    templateUrl: './geographical-summary-page.component.html',
    styleUrls: ['./geographical-summary-page.component.scss']
})
export class GeographicalSummaryPageComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly preferredRegionOrder = [3, 4, 5, 6, 8, 9, 10, 11, 12];
    private readonly europeRegionId = 3;
    private readonly unitedKingdomIso = 'GB';
    private readonly unitedStatesIso = 'US';
    private readonly unitedArabEmiratesIso = 'AE';
    private readonly isleOfManIso = 'IM';
    private readonly ukConstituentCountryIds = {
        england: 308,
        scotland: 303,
        wales: 305,
        northernIreland: 307,
        isleOfMan: 990001
    };
    private readonly constituentDrilldownConfigs: Record<string, {
        label: string;
        geometryUrl: string;
        regionId: number;
    }> = {
            GB: {
                label: 'United Kingdom constituents',
                geometryUrl: '/assets/geographical-summary/geoJson/custom.geo_UKConstituents.json',
                regionId: 3
            },
            AE: {
                label: 'United Arab Emirates emirates',
                geometryUrl: '/assets/geographical-summary/geoJson/custom.geo_UAEConstituents.json',
                regionId: 4
            },
            US: {
                label: 'United States constituent states',
                geometryUrl: '/assets/geographical-summary/geoJson/custom.geo_USAConstituents.json',
                regionId: 11
            }
        };

    devRegionIdInput = '';
    devCountryIdInput = '';
    devRegionSelectorValue = '';
    devCountrySelectorValue = '';
    devLegacyRegionNames: string[] = [];
    devLegacyRegions: LegacySeedRegion[] = [];

    summaryErrorMessage: string | null = null;
    chartErrorMessage: string | null = null;
    projectsErrorMessage: string | null = null;

    sectors: GeographicalSector[] = [];
    selectedSectorId: number | null = null;

    countries: GeographicalCountrySummary[] = [];
    selectedRegionId: number | null = null;
    selectedCountryId: number | null = null;

    chartPoints: GeographicalChartPoint[] = [];
    allProjects: GeographicalProjectLink[] = [];
    projects: GeographicalProjectLink[] = [];
    activeChartFilter: { startupYear: number; stageKey: string } | null = null;

    mapGeometry: GeoMapFeatureCollection = { type: 'FeatureCollection', features: [] };
    mapRegionGeometry: GeoMapRegionFeatureCollection = { type: 'FeatureCollection', features: [] };
    private mapGeometrySource: GeoMapFeatureCollection = { type: 'FeatureCollection', features: [] };
    private mapRegionGeometrySource: GeoMapRegionFeatureCollection = { type: 'FeatureCollection', features: [] };
    private constituentDrilldownGeometrySources: Record<string, GeoMapFeatureCollection | null> = {
        GB: null,
        AE: null,
        US: null
    };
    private activeConstituentDrilldownIso: string | null = null;
    ukDrilldownGeometry: GeoMapFeatureCollection = { type: 'FeatureCollection', features: [] };
    ukDrilldownEnabled = false;
    selectedUkConstituentId: number | null = null;
    ukNonInteractiveCountryIds: number[] = [];

    flyoutProjectId: number | null = null;
    flyoutProjectName: string | null = null;
    flyoutProject$: Observable<IProject> | undefined;
    flyoutContainerHeightPx: number | null = null;
    projectsListMaxHeightPx: number | null = null;

    @ViewChild('flyoutContainer', { static: true })
    private flyoutContainerRef?: ElementRef<HTMLElement>;

    @ViewChild('projectsListScroll')
    set projectsListScrollRef(element: ElementRef<HTMLElement> | undefined) {
        this.projectsListElementRef = element;
        this.updateProjectsListMaxHeight();
    }

    private projectsListElementRef?: ElementRef<HTMLElement>;

    private bodyLayoutObserver?: MutationObserver;

    constructor(
        private http: HttpClient,
        private transition: Transition,
        private stateService: StateService,
        private geoSummaryData: GeographicalSummaryDataService,
        private geoMapData: GeographicalMapDataService,
        private projectData: ProjectDataService
    ) { }

    ngOnInit(): void {
        this.applySelectionsFromParams();
        this.devRegionIdInput = this.selectedRegionId ? String(this.selectedRegionId) : '';
        this.devCountryIdInput = this.selectedCountryId ? String(this.selectedCountryId) : '';
        this.devRegionSelectorValue = this.selectedRegionId ? String(this.selectedRegionId) : '';
        this.devCountrySelectorValue = this.selectedCountryId ? String(this.selectedCountryId) : '';
        this.loadDevLegacySeed();
        this.loadConstituentDrilldownGeometries();
        this.loadSummary();
        this.geoMapData.getGeometry().subscribe((geometry) => {
            this.mapGeometrySource = geometry || { type: 'FeatureCollection', features: [] };
            this.syncMapData();
        });
        this.geoMapData.getRegionGeometry().subscribe((geometry) => {
            this.mapRegionGeometrySource = geometry || { type: 'FeatureCollection', features: [] };
            this.syncMapData();
        });

        if (this.selectedCountryId) {
            this.loadCountryData();
        }

        const projectId = this.toPositiveInt(this.transition.params().projectID);
        const projectName = this.transition.params().projectName as string;
        this.setFlyoutProject(projectId, projectName || null);
    }

    ngAfterViewInit(): void {
        this.updateFlyoutContainerHeight();
        this.attachBodyLayoutObserver();
    }

    ngOnDestroy(): void {
        this.bodyLayoutObserver?.disconnect();
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.updateFlyoutContainerHeight();
    }

    onFlyoutOpened(): void {
        this.updateFlyoutContainerHeight();
    }

    onSectorChanged(value: string): void {
        const id = Number(value);
        this.selectedSectorId = Number.isFinite(id) && id > 0 ? id : null;
        this.selectedRegionId = null;
        this.selectedCountryId = null;
        this.devRegionSelectorValue = '';
        this.devCountrySelectorValue = '';
        this.chartPoints = [];
        this.allProjects = [];
        this.projects = [];
        this.activeChartFilter = null;
        this.loadSummary();
        this.updateRouteState();
    }

    onRegionSelectorChanged(value: string): void {
        this.devRegionSelectorValue = value || '';
        this.devCountrySelectorValue = '';
        this.exitConstituentDrilldown();

        const id = Number(value);
        if (Number.isFinite(id) && id > 0) {
            this.onRegionSelected(id);
            return;
        }

        this.selectedRegionId = null;
        this.selectedCountryId = null;
        this.chartPoints = [];
        this.allProjects = [];
        this.projects = [];
        this.activeChartFilter = null;
        this.loadSummary();
        this.updateRouteState();
    }

    onCountrySelectorChanged(value: string): void {
        this.devCountrySelectorValue = value || '';
        this.exitConstituentDrilldown();

        const id = Number(value);
        if (Number.isFinite(id) && id > 0) {
            this.selectedCountryId = id;
            const selectedCountry = this.countries.find((country) => country.eicCountryId === this.selectedCountryId);
            this.selectedRegionId = selectedCountry?.eicRegionId || this.selectedRegionId;
            this.devRegionSelectorValue = this.selectedRegionId ? String(this.selectedRegionId) : this.devRegionSelectorValue;
            this.loadCountryData();
            this.updateRouteState();
            return;
        }

        const prefixedName = value.startsWith('legacyCountry:') ? value.replace('legacyCountry:', '') : '';
        const matchedByName = prefixedName
            ? this.countries.find((country) => this.normaliseCountryName(country.countryName) === this.normaliseCountryName(prefixedName))
            : null;

        this.selectedCountryId = matchedByName?.eicCountryId || null;

        if (matchedByName) {
            this.selectedRegionId = matchedByName.eicRegionId;
            this.devRegionSelectorValue = String(matchedByName.eicRegionId);
            this.devCountrySelectorValue = String(matchedByName.eicCountryId);
            this.loadCountryData();
        } else {
            this.chartPoints = [];
            this.allProjects = [];
            this.projects = [];
            this.activeChartFilter = null;
        }

        this.updateRouteState();
    }

    onCountrySelected(selection: GeoMapCountrySelection): void {
        if (!selection) {
            return;
        }

        if (this.ukDrilldownEnabled) {
            this.selectedUkConstituentId = selection.eicCountryId > 0 ? selection.eicCountryId : null;
            this.selectedCountryId = this.selectedUkConstituentId;
            this.devCountrySelectorValue = this.selectedUkConstituentId ? String(this.selectedUkConstituentId) : '';
            this.loadCountryData();
            this.updateRouteState();
            return;
        }

        const resolvedCountry = this.resolveCountryFromMapSelection(selection);
        if (resolvedCountry) {
            const drilldownIso = this.resolveConstituentDrilldownIso(resolvedCountry, selection);
            if (drilldownIso) {
                this.enterConstituentDrilldown(drilldownIso, resolvedCountry.eicRegionId);
                this.updateRouteState();
                return;
            }

            this.selectedRegionId = resolvedCountry.eicRegionId;
            this.selectedCountryId = resolvedCountry.eicCountryId;
            this.devRegionSelectorValue = String(resolvedCountry.eicRegionId);
            this.devCountrySelectorValue = String(resolvedCountry.eicCountryId);
        } else {
            if (this.countries.length) {
                this.selectedCountryId = null;
                this.devCountrySelectorValue = '';
                this.chartPoints = [];
                this.allProjects = [];
                this.projects = [];
                this.activeChartFilter = null;
                this.updateRouteState();
                return;
            }

            if (selection.eicRegionId && selection.eicRegionId > 0) {
                this.selectedRegionId = selection.eicRegionId;
                this.devRegionSelectorValue = String(selection.eicRegionId);
            }

            this.selectedCountryId = selection.eicCountryId;
            this.devCountrySelectorValue = String(selection.eicCountryId);
        }

        this.loadCountryData();
        this.updateRouteState();
    }

    applyManualDevIds(): void {
        this.selectedRegionId = this.toPositiveInt(this.devRegionIdInput);
        this.selectedCountryId = this.toPositiveInt(this.devCountryIdInput);
        this.exitConstituentDrilldown();
        this.devRegionSelectorValue = this.selectedRegionId ? String(this.selectedRegionId) : '';
        this.devCountrySelectorValue = this.selectedCountryId ? String(this.selectedCountryId) : '';

        this.chartPoints = [];
        this.allProjects = [];
        this.projects = [];
        this.activeChartFilter = null;

        this.loadSummary();
        if (this.selectedCountryId) {
            this.loadCountryData();
        }

        this.updateRouteState();
    }

    onRegionSelected(regionId: number): void {
        this.selectedRegionId = regionId > 0 ? regionId : null;
        this.exitConstituentDrilldown();
        this.devRegionSelectorValue = this.selectedRegionId ? String(this.selectedRegionId) : '';
        this.devCountrySelectorValue = '';
        this.selectedCountryId = null;
        this.chartPoints = [];
        this.allProjects = [];
        this.projects = [];
        this.activeChartFilter = null;
        this.loadSummary();
        this.updateRouteState();
    }

    clearRegionSelection(): void {
        this.onRegionSelected(0);
    }

    backToEuropeMap(): void {
        this.exitConstituentDrilldown();
        this.selectedCountryId = null;
        this.devCountrySelectorValue = '';
        this.chartPoints = [];
        this.allProjects = [];
        this.projects = [];
        this.activeChartFilter = null;
        this.updateRouteState();
    }

    onChartPointSelected(point: GeographicalChartPoint): void {
        this.activeChartFilter = {
            startupYear: point.startupYear,
            stageKey: point.stageKey
        };
        this.applyProjectFilter();
    }

    clearChartFilter(): void {
        this.activeChartFilter = null;
        this.projects = [...this.allProjects];
    }

    onProjectSelected(project: GeographicalProjectLink): void {
        if (!project?.projectId) {
            return;
        }

        this.setFlyoutProject(project.projectId, project.projectName || null);
        this.updateRouteState();
    }

    closeFlyout(): void {
        this.setFlyoutProject(null);
        this.updateRouteState();
    }

    get selectedCountry(): GeographicalCountrySummary | null {
        if (!this.selectedCountryId) {
            return null;
        }

        return this.countries.find((country) => country.eicCountryId === this.selectedCountryId) || null;
    }

    get regionSelectorOptions(): Array<{ id: number; name: string }> {
        if (this.devLegacyRegions.length) {
            return this.devLegacyRegions.map((region) => {
                const mappedRegionId = this.resolveLegacyRegionId(region);
                return {
                    id: Number(mappedRegionId || 0),
                    name: mappedRegionId ? region.name : `${region.name} (no DS5 ID mapping yet)`
                };
            });
        }

        const mapRegionIds = (this.mapRegionGeometry?.features || [])
            .map((feature) => Number(feature?.properties?.eicRegionId) || 0)
            .filter((id) => id > 0);
        const countryRegionIds = this.countries.map((country) => country.eicRegionId);
        const regionIds = this.sortRegionIds(Array.from(new Set([...mapRegionIds, ...countryRegionIds])));

        return regionIds.map((id) => {
            const feature = this.mapRegionGeometry.features.find((region) => region?.properties?.eicRegionId === id);
            const name = (feature?.properties?.name as string) || `Region ${id}`;

            return { id, name };
        });
    }

    get countrySelectorOptions(): DevCountryOption[] {
        if (this.devLegacyRegions.length) {
            const selectedLegacyRegion = this.devLegacyRegions.find((region) => {
                const mappedRegionId = this.resolveLegacyRegionId(region);
                return mappedRegionId ? String(mappedRegionId) === this.devRegionSelectorValue : false;
            });

            const scopedLegacyCountries = selectedLegacyRegion
                ? selectedLegacyRegion.countries
                : this.devLegacyRegions.flatMap((region) => region.countries);

            return scopedLegacyCountries
                .map((country) => (country.name || '').trim())
                .filter((name) => !!name)
                .map((countryName) => {
                    const matched = this.countries.find(
                        (country) => this.normaliseCountryName(country.countryName) === this.normaliseCountryName(countryName)
                    );

                    return {
                        value: matched ? String(matched.eicCountryId) : `legacyCountry:${countryName}`,
                        name: matched ? countryName : `${countryName} (no DS5 ID mapping yet)`,
                        mapped: !!matched
                    };
                });
        }

        const countries = this.selectedRegionId
            ? this.countries.filter((country) => country.eicRegionId === this.selectedRegionId)
            : this.countries;

        return countries.map((country) => ({
            value: String(country.eicCountryId),
            name: country.countryName,
            mapped: true
        }));
    }

    private sortRegionIds(regionIds: number[]): number[] {
        const rank = new Map<number, number>();
        this.preferredRegionOrder.forEach((id, index) => rank.set(id, index));

        return regionIds.sort((left, right) => {
            const leftRank = rank.has(left) ? rank.get(left)! : Number.MAX_SAFE_INTEGER;
            const rightRank = rank.has(right) ? rank.get(right)! : Number.MAX_SAFE_INTEGER;
            if (leftRank !== rightRank) {
                return leftRank - rightRank;
            }

            return left - right;
        });
    }

    get mapFills(): GeoMapFill[] {
        return this.visibleCountries.map((country) => ({
            eicCountryId: country.eicCountryId,
            fill: country.colorHex || 'var(--sys-surface-container-high)'
        }));
    }

    get visibleCountries(): GeographicalCountrySummary[] {
        if (!this.selectedRegionId) {
            return this.countries;
        }

        return this.countries.filter((country) => country.eicRegionId === this.selectedRegionId);
    }

    get isUkConstituentDrilldown(): boolean {
        return this.ukDrilldownEnabled;
    }

    get constituentDrilldownLabel(): string {
        if (!this.activeConstituentDrilldownIso) {
            return '';
        }

        return this.constituentDrilldownConfigs[this.activeConstituentDrilldownIso]?.label || '';
    }

    get canGoBackToEurope(): boolean {
        return this.ukDrilldownEnabled;
    }

    get legendMinLabel(): string {
        if (!this.visibleCountries.length) {
            return '0';
        }

        const min = Math.min(...this.visibleCountries.map((country) => Number(country.capex) || 0));
        return this.formatLegendValue(min);
    }

    get legendMaxLabel(): string {
        if (!this.visibleCountries.length) {
            return '0';
        }

        const max = Math.max(...this.visibleCountries.map((country) => Number(country.capex) || 0));
        return this.formatLegendValue(max);
    }

    /**
     * Returns true if a country (or UK constituent) is selected.
     */
    get hasCountrySelected(): boolean {
        return !!(this.selectedCountryId || this.selectedUkConstituentId);
    }

    /**
     * Returns the map title based on current selection state.
     * Examples: "World Map", "Europe", "United Kingdom"
     */
    get mapTitle(): string {
        if (this.isUkConstituentDrilldown) {
            return this.constituentDrilldownLabel || 'World Map';
        }

        if (this.selectedRegionId) {
            const feature = this.mapRegionGeometry.features.find(
                (region) => region?.properties?.eicRegionId === this.selectedRegionId
            );
            const regionName = (feature?.properties?.name as string) || `Region ${this.selectedRegionId}`;
            return regionName;
        }

        return 'World Map';
    }

    private loadSummary(): void {
        this.summaryErrorMessage = null;
        this.geoSummaryData.getSummary(this.selectedSectorId, this.selectedRegionId, this.selectedCountryId).subscribe((summary) => {
            this.sectors = summary.sectors || [];
            this.countries = summary.countries || [];
            this.syncMapData();

            if (this.selectedRegionId) {
                const hasRegion = this.countries.some((country) => country.eicRegionId === this.selectedRegionId);
                if (!hasRegion) {
                    this.selectedRegionId = null;
                }
            }

            if (this.selectedCountryId) {
                const hasCountry = this.countries.some((country) => country.eicCountryId === this.selectedCountryId);
                if (!hasCountry) {
                    this.selectedCountryId = null;
                }
            }
        }, (error) => {
            this.summaryErrorMessage = this.buildErrorMessage('geographical summary data', error);
            this.sectors = [];
            this.countries = [];
            this.syncMapData();
            this.selectedRegionId = null;
            this.selectedCountryId = null;
            this.chartPoints = [];
            this.allProjects = [];
            this.projects = [];
            this.activeChartFilter = null;
        });
    }

    private loadCountryData(): void {
        this.chartErrorMessage = null;
        this.projectsErrorMessage = null;

        if (!this.selectedCountryId) {
            this.chartPoints = [];
            this.allProjects = [];
            this.projects = [];
            this.activeChartFilter = null;
            return;
        }

        this.geoSummaryData.getChart(this.selectedCountryId, this.selectedSectorId).subscribe((points) => {
            this.chartPoints = points;
        }, (error) => {
            this.chartErrorMessage = this.buildErrorMessage('chart data for the selected country', error);
            this.chartPoints = [];
        });

        this.geoSummaryData.getProjects(this.selectedCountryId, this.selectedSectorId).subscribe((projects) => {
            this.allProjects = projects;
            this.applyProjectFilter();
        }, (error) => {
            this.projectsErrorMessage = this.buildErrorMessage('projects for the selected country', error);
            this.allProjects = [];
            this.projects = [];
            this.activeChartFilter = null;
        });
    }

    private applyProjectFilter(): void {
        if (!this.activeChartFilter) {
            this.projects = [...this.allProjects];
            return;
        }

        this.projects = this.allProjects.filter(
            (project) =>
                project.startupYear === this.activeChartFilter!.startupYear
                && project.projectStage === this.activeChartFilter!.stageKey
        );
    }

    private applySelectionsFromParams(): void {
        this.selectedSectorId = this.toPositiveInt(this.transition.params().sectorId);
        this.selectedRegionId = null;
        this.selectedCountryId = null;
    }

    private loadDevLegacySeed(): void {
        this.http
            .get<{ regions?: LegacySeedRegion[] }>('/assets/geographical-summary/dev-legacy-map-seed.json')
            .subscribe((seed) => {
                this.devLegacyRegions = (seed?.regions || []).map((region) => ({
                    legacyRegionId: region.legacyRegionId,
                    name: region.name,
                    countries: region.countries || []
                }));

                this.devLegacyRegionNames = this.devLegacyRegions
                    .map((region) => (region.name || '').trim())
                    .filter((name) => !!name);
            });
    }

    private resolveLegacyRegionId(region: LegacySeedRegion): number | null {
        const targetNames = new Set(
            (region.countries || [])
                .map((country) => this.normaliseCountryName(country.name || ''))
                .filter((name) => !!name)
        );

        const matches = this.countries.filter((country) => targetNames.has(this.normaliseCountryName(country.countryName)));
        if (!matches.length) {
            return null;
        }

        const counts = matches.reduce((accumulator, country) => {
            accumulator.set(country.eicRegionId, (accumulator.get(country.eicRegionId) || 0) + 1);
            return accumulator;
        }, new Map<number, number>());

        let bestRegionId: number | null = null;
        let bestCount = -1;
        counts.forEach((count, regionId) => {
            if (count > bestCount) {
                bestCount = count;
                bestRegionId = regionId;
            }
        });

        return bestRegionId;
    }

    private normaliseCountryName(value: string): string {
        return (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    }

    private resolveCountryFromMapSelection(selection: GeoMapCountrySelection): GeographicalCountrySummary | null {
        if (selection.eicCountryId > 0) {
            const exactCountry = this.countries.find((country) => country.eicCountryId === selection.eicCountryId);
            if (exactCountry) {
                return exactCountry;
            }
        }

        const mapSid = (selection.sid || '').toUpperCase();
        if (mapSid) {
            const sidMatch = this.countries.find((country) => (country.sid || '').toUpperCase() === mapSid);
            if (sidMatch) {
                return sidMatch;
            }
        }

        const mapName = this.normaliseCountryName(selection.countryName || '');
        if (!mapName) {
            return null;
        }

        return this.countries.find((country) => this.normaliseCountryName(country.countryName) === mapName) || null;
    }

    private syncMapData(): void {
        const enrichedGeometry = this.enrichMapGeometryWithCountryIds(this.mapGeometrySource);
        const activeGeometry = this.activeConstituentDrilldownIso
            ? this.constituentDrilldownGeometrySources[this.activeConstituentDrilldownIso]
            : null;

        this.ukDrilldownGeometry = activeGeometry || { type: 'FeatureCollection', features: [] };

        if (this.activeConstituentDrilldownIso === this.unitedKingdomIso) {
            this.ukNonInteractiveCountryIds = [this.ukConstituentCountryIds.isleOfMan];
        } else {
            this.ukNonInteractiveCountryIds = [];
        }

        this.mapGeometry = this.buildEuropeUkAggregateGeometry(enrichedGeometry);
        if (this.mapRegionGeometrySource?.features?.length) {
            this.mapRegionGeometry = this.mapRegionGeometrySource;
            return;
        }

        this.mapRegionGeometry = { type: 'FeatureCollection', features: [] };
    }

    private loadConstituentDrilldownGeometries(): void {
        Object.entries(this.constituentDrilldownConfigs).forEach(([iso, config]) => {
            this.http
                .get<GeoMapFeatureCollection>(config.geometryUrl)
                .subscribe((geometry) => {
                    const normalized = this.normaliseConstituentGeometry(geometry, config.regionId);
                    this.constituentDrilldownGeometrySources[iso] = normalized;
                    if (!normalized.features.length) {
                        console.warn(
                            '[GeographicalSummary] Constituent geometry loaded but has no renderable features',
                            { iso, geometryUrl: config.geometryUrl }
                        );
                    }
                    this.syncMapData();
                }, (error) => {
                    console.error(
                        '[GeographicalSummary] Failed to load constituent drilldown geometry',
                        { iso, geometryUrl: config.geometryUrl, error }
                    );
                    this.constituentDrilldownGeometrySources[iso] = null;
                    this.syncMapData();
                });
        });
    }

    private normaliseConstituentGeometry(geometry: GeoMapFeatureCollection, regionId: number): GeoMapFeatureCollection {
        const features = (geometry?.features || [])
            .filter((feature: any) => feature?.geometry && feature?.properties)
            .filter((feature: any) => this.hasRenderableGeometry(feature.geometry as any))
            .map((feature: any) => ({
                ...feature,
                properties: {
                    ...feature.properties,
                    eicRegionId: regionId,
                    eicCountryId: Number(feature.properties.eicCountryId) || 0,
                    sid: String(feature.properties.sid || feature.properties.iso2 || '').toUpperCase() || undefined,
                    name: String(feature.properties.name || '').trim()
                }
            }));

        return {
            type: 'FeatureCollection',
            features
        };
    }

    private enrichMapGeometryWithCountryIds(geometry: GeoMapFeatureCollection): GeoMapFeatureCollection {
        const features = (geometry?.features || []).map((feature: any) => {
            const properties = feature?.properties || {};
            const sid = String(properties.sid || properties.iso2 || '').toUpperCase();
            const mapName = this.normaliseCountryName(properties.name || '');

            const country = this.countries.find((candidate) => {
                const candidateSid = String(candidate.sid || '').toUpperCase();
                if (sid && candidateSid && sid === candidateSid) {
                    return true;
                }

                return this.normaliseCountryName(candidate.countryName) === mapName;
            });

            if (!country) {
                return feature;
            }

            return {
                ...feature,
                properties: {
                    ...properties,
                    eicCountryId: country.eicCountryId,
                    eicRegionId: country.eicRegionId,
                    sid: country.sid || sid || undefined,
                    name: country.countryName || properties.name
                }
            };
        });

        return {
            type: 'FeatureCollection',
            features
        };
    }

    private buildEuropeUkAggregateGeometry(geometry: GeoMapFeatureCollection): GeoMapFeatureCollection {
        const features = geometry?.features || [];
        const ukFeature = features.find((feature) => this.getFeatureIso2(feature) === this.unitedKingdomIso);
        const isleOfManFeature = features.find((feature) => this.getFeatureIso2(feature) === this.isleOfManIso);

        if (!ukFeature || !isleOfManFeature) {
            return geometry;
        }

        const ukPolygons = this.geometryToPolygons(ukFeature.geometry as any);
        const isleOfManPolygons = this.geometryToPolygons(isleOfManFeature.geometry as any);
        const mergedPolygons = [...ukPolygons, ...isleOfManPolygons];
        if (!mergedPolygons.length) {
            return geometry;
        }

        const mergedUkFeature: any = {
            ...ukFeature,
            geometry: mergedPolygons.length === 1
                ? { type: 'Polygon', coordinates: mergedPolygons[0] }
                : { type: 'MultiPolygon', coordinates: mergedPolygons },
            properties: {
                ...ukFeature.properties,
                name: 'United Kingdom'
            }
        };

        const filtered = features.filter((feature) => {
            const iso = this.getFeatureIso2(feature);
            return iso !== this.unitedKingdomIso && iso !== this.isleOfManIso;
        });

        filtered.push(mergedUkFeature);

        return {
            type: 'FeatureCollection',
            features: filtered
        };
    }

    private geometryToPolygons(geometry: any): number[][][][] {
        if (!geometry?.type || !Array.isArray(geometry.coordinates)) {
            return [];
        }

        if (geometry.type === 'Polygon') {
            return [geometry.coordinates as number[][][]];
        }

        if (geometry.type === 'MultiPolygon') {
            return geometry.coordinates as number[][][][];
        }

        return [];
    }

    private hasRenderableGeometry(geometry: any): boolean {
        const polygons = this.geometryToPolygons(geometry);
        if (!polygons.length) {
            return false;
        }

        return polygons.some((polygon) =>
            polygon.some((ring) =>
                Array.isArray(ring)
                && ring.length >= 3
                && ring.every((point) => Array.isArray(point)
                    && point.length >= 2
                    && Number.isFinite(Number(point[0]))
                    && Number.isFinite(Number(point[1])))
            )
        );
    }

    private getFeatureIso2(feature: any): string {
        const properties = feature?.properties || {};
        const candidates = [properties.iso2, properties.sid, properties.postal, properties.iso_a2, properties.iso_a2_eh];
        for (const candidate of candidates) {
            const value = String(candidate || '').trim().toUpperCase();
            if (value && value !== 'NULL' && value !== '-99' && value.length === 2) {
                return value;
            }
        }

        return '';
    }

    private resolveConstituentDrilldownIso(
        resolvedCountry: GeographicalCountrySummary,
        selection: GeoMapCountrySelection
    ): string | null {
        const sid = String(resolvedCountry.sid || selection.sid || '').trim().toUpperCase();
        const name = this.normaliseCountryName(resolvedCountry.countryName || selection.countryName || '');

        if (sid === this.unitedKingdomIso || sid === 'UK' || name.includes('unitedkingdom')) {
            if (this.isConstituentDrilldownAvailable(this.unitedKingdomIso)) {
                return this.unitedKingdomIso;
            }

            console.warn('[GeographicalSummary] UK drilldown unavailable, staying on region map.');
            return null;
        }

        if (sid === this.unitedArabEmiratesIso || name.includes('unitedarabemirates')) {
            if (this.isConstituentDrilldownAvailable(this.unitedArabEmiratesIso)) {
                return this.unitedArabEmiratesIso;
            }

            console.warn('[GeographicalSummary] UAE drilldown unavailable, staying on region map.');
            return null;
        }

        if (sid === this.unitedStatesIso || sid === 'USA' || name === 'usa' || name.includes('unitedstates')) {
            if (this.isConstituentDrilldownAvailable(this.unitedStatesIso)) {
                return this.unitedStatesIso;
            }

            console.warn('[GeographicalSummary] USA drilldown unavailable, staying on region map.');
            return null;
        }

        return null;
    }

    private isConstituentDrilldownAvailable(countryIso: string): boolean {
        const geometry = this.constituentDrilldownGeometrySources[countryIso];
        return !!geometry?.features?.length;
    }

    private enterConstituentDrilldown(countryIso: string, regionId: number): void {
        if (!this.isConstituentDrilldownAvailable(countryIso)) {
            return;
        }

        this.selectedRegionId = regionId > 0 ? regionId : this.selectedRegionId;
        this.activeConstituentDrilldownIso = countryIso;
        this.ukDrilldownEnabled = true;
        this.selectedUkConstituentId = null;
        this.selectedCountryId = null;
        this.devRegionSelectorValue = this.selectedRegionId ? String(this.selectedRegionId) : this.devRegionSelectorValue;
        this.devCountrySelectorValue = '';
        this.chartPoints = [];
        this.allProjects = [];
        this.projects = [];
        this.activeChartFilter = null;
        this.syncMapData();
    }

    private exitConstituentDrilldown(): void {
        this.activeConstituentDrilldownIso = null;
        this.ukDrilldownEnabled = false;
        this.selectedUkConstituentId = null;
        this.syncMapData();
    }

    private setFlyoutProject(projectId: number | null, projectName: string | null = null): void {
        this.flyoutProjectId = projectId;
        this.flyoutProjectName = projectName;
        this.flyoutProject$ = projectId ? this.projectData.getProject(projectId) : undefined;
        this.updateFlyoutContainerHeight();
    }

    private attachBodyLayoutObserver(): void {
        if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') {
            return;
        }

        this.bodyLayoutObserver = new MutationObserver(() => {
            this.updateFlyoutContainerHeight();
        });

        this.bodyLayoutObserver.observe(document.body, {
            attributes: true,
            attributeFilter: ['class', 'style'],
            childList: true
        });
    }

    private updateFlyoutContainerHeight(): void {
        if (typeof window === 'undefined') {
            return;
        }

        const container = this.flyoutContainerRef?.nativeElement;
        if (!container) {
            return;
        }

        const top = Math.max(container.getBoundingClientRect().top, 0);
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        this.flyoutContainerHeightPx = Math.max(Math.floor(viewportHeight - top), 0);
        this.updateProjectsListMaxHeight();
    }

    private updateProjectsListMaxHeight(): void {
        if (typeof window === 'undefined') {
            return;
        }

        const listElement = this.projectsListElementRef?.nativeElement;
        if (!listElement) {
            this.projectsListMaxHeightPx = null;
            return;
        }

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        const listTop = listElement.getBoundingClientRect().top;
        const bottomPadding = 16;
        this.projectsListMaxHeightPx = Math.max(Math.floor(viewportHeight - listTop - bottomPadding), 140);
    }

    private updateRouteState(): void {
        const targetState = (this.transition.to() as any)?.name;
        if (!targetState) {
            return;
        }

        const currentParams = this.transition.params() || {};
        this.stateService.go(targetState, {
            ...currentParams,
            sectorId: this.selectedSectorId || undefined,
            regionId: this.selectedRegionId || undefined,
            countryId: this.selectedCountryId || undefined,
            projectID: this.flyoutProjectId || undefined,
            projectName: this.flyoutProjectName || undefined
        }, {
            notify: false,
            location: 'replace'
        });
    }

    private toPositiveInt(value: unknown): number | null {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }

    private formatLegendValue(value: number): string {
        if (!Number.isFinite(value)) {
            return '0';
        }

        if (Math.abs(value) >= 1_000_000) {
            return `${(value / 1_000_000).toFixed(1)}M`;
        }

        if (Math.abs(value) >= 1_000) {
            return `${(value / 1_000).toFixed(1)}K`;
        }

        return value.toFixed(0);
    }

    private buildErrorMessage(dataLabel: string, error: any): string {
        const status = error?.status;
        if (status === 502 || status === 503 || status === 504) {
            return `Unable to reach the server. The ${dataLabel} service may be temporarily unavailable. Please try again in a moment.`;
        }
        if (status === 401) {
            return `Unauthorized access to ${dataLabel}. Please check your login credentials and try again.`;
        }
        if (status === 403) {
            return `Access denied to ${dataLabel}. You may not have permission to view this data.`;
        }
        if (status === 404) {
            return `Unable to find ${dataLabel}. The requested resource does not exist.`;
        }
        if (status === 400) {
            return `Invalid request for ${dataLabel}. Please check your filters and try again.`;
        }
        return `Unable to load ${dataLabel}. Please try again.`;
    }

}
